<template>
  <article>
    <div class="resort-item resort-item-large">
      <search-item-images :resort="resort" />

      <a :href="supplierURL" target="_blank" class="relative">
        <div class="price-cta">
          <div class="fs-16 my-2">
            from
            <strong class="fs-20 font-medium leading-tight">
              <currency-context>
                <template v-slot="{ convert }">
                  <span
                    v-text="
                      convert(resort.__odyssey_price_per_day.toLocaleString())
                    "
                  ></span>
                </template>
              </currency-context>
            </strong>
            /day
          </div>
          <button class="btn btn-primary">View boat</button>
        </div>
        <h4 class="fs-20 font-medium flex items-center">
          <span v-html="resort.__platform_name" />
          <booking-term-icon
            v-if="hasBookingTerm"
            :type="bookingTerm.type"
            class="ml-2 fill-blue"
          />
        </h4>

        <p
          class="mt-1 text-black-light-5 fs-14"
          v-html="resort.__platform_country_title"
        ></p>

        <div class="mt-2 flex items-end" v-if="!!resort.rating">
          <rating
            :value="resort.rating.supplier_rating"
            class="mr-1"
            size="28"
          />
          <span class="fs-14 text-black-light-5"
            >{{ resort.rating.count }}
            {{ resort.rating.count > 1 ? "reviews" : "review" }}</span
          >
        </div>

        <div class="flex items-center mt-2 price-info" v-else>
          <div class="flex items-center flex-1" v-if="resort.__platform_rating">
            <img
              :src="tripadvisorImage"
              :alt="resort.__platform_name"
              width="119"
              height="20"
              class="review-image max-w-none"
            />
            <a
              :href="resort.__platform_tripadvisor_url"
              class="review-text underline text-black-light-5 text-sm leading-none"
            >
              <span
                >{{ resort.__platform_tripadvisor_total_review }} reviews</span
              >
            </a>
          </div>
        </div>

        <div v-if="displayingFacilities.length" class="text-black-light-5 mt-4">
          <ul class="list-reset flex items-center flex-wrap">
            <li
              v-for="(item, index) in displayingFacilities"
              :key="index"
              class="mr-4 has-tooltip flex items-center"
            >
              <span class="tooltip rounded shadow-lg py-1 px-4 fs-16">{{
                item.name === "Internet" ? "Wifi" : item.name
              }}</span>
              <img width="28" height="28" :src="item.icon" :alt="item.name" />
              <span class="ml-1">{{
                item.name === "Internet" ? "Wifi" : item.name
              }}</span>
            </li>
          </ul>
        </div>
        <p :class="displayingFacilities.length ? 'mt-4' : 'mt-10'">
          {{ resort.__platform_description }}
        </p>

        <div
          class="mt-4"
          v-if="
            resort.__odyssey_greenfins_membership_level ||
              resort.__odyssey_zublu_featured
          "
        >
          <div
            v-if="resort.__odyssey_greenfins_membership_level !== 'none'"
            class="greenfin-tag mr-2 capitalize"
            :class="`${resort.__odyssey_greenfins_membership_level}-color`"
          >
            Green Fins {{ resort.__odyssey_greenfins_membership_level }} Member
          </div>
          <div v-if="resort.__odyssey_zublu_featured" class="top-pick-tag mr-2">
            Our top pick
          </div>
        </div>
      </a>
    </div>

    <a :href="supplierURL" target="_blank">
      <ul
        class="list-reset trip-list mt-4 fs-14"
        v-if="tripsForSelectedMonthAndFilters.length"
      >
        <li
          v-for="trip in tripsForSelectedMonthAndFilters"
          :key="trip.id"
          class="trip-item"
          :class="getTripClass(trip)"
        >
          <span class="font-medium">
            {{ trip.date | tripDate }}
          </span>
          <span class="truncate" :title="trip.trip_name">
            {{ trip.trip_name }}
          </span>
          <div class="text-center">
            <svg
              width="24"
              height="24"
              class="inline-block"
              fill="currentColor"
            >
              <use xlink:href="/img/sprite.svg#ic_calendar"></use>
            </svg>
            {{ trip.length + 1 }}D / {{ trip.length }}N
          </div>
          <div class="truncate">
            <svg
              width="24"
              height="24"
              class="inline-block"
              fill="currentColor"
            >
              <use xlink:href="/img/sprite.svg#ic_diving"></use>
            </svg>
            {{
              trip.dive_number === "0"
                ? "No diving"
                : `${trip.dive_number} dives`
            }}
          </div>
          <div class="font-medium text-center availablity-text lh-150 flex">
            <span class="m-auto">
              {{ trip.availability | formatAvailability }}
            </span>
          </div>
          <div class="text-black-light-2">|</div>
          <div class="relative">
            <div class="discount-tag" v-if="hasDiscount(trip)">
              <template
                v-if="
                  trip.discount_method === 'fix_amount' && trip.discount_price
                "
              >
                -
                <currency-context>
                  <template v-slot="{ convert }">
                    <span v-text="convert(trip.discount_price)"></span>
                  </template>
                </currency-context>
              </template>
              <template
                v-else-if="
                  trip.discount_method === 'percentage' && trip.discount_price
                "
              >
                {{ trip.discount_price }}% off
              </template>
              <template v-else-if="trip.discount_method === 'custom'">
                Special rate
              </template>
            </div>

            <div
              class="relative flex price-box"
              :data-discount-method="trip.discount_method"
            >
              <span class="font-medium">
                <currency-context>
                  <template v-slot="{ convert }">
                    <span v-text="convert(trip.price.price)"></span>
                  </template>
                </currency-context> </span
              >&nbsp;/guest

              <div
                class="old-price line-through"
                v-if="hasDiscount(trip) && trip.discount_price"
              >
                <currency-context>
                  <template v-slot="{ convert }">
                    <span v-text="convert(trip.price.real_price)"></span>
                  </template>
                </currency-context>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </a>
  </article>
</template>

<script>
import Rating from "../core/Rating";
import LiveaboardItemMixin from "../../mixins/LiveaboardItemMixin";
import SearchItemImages from "./SearchItemImages";
import BookingTermIcon from "./mixins/BookingTermIcon";
import CurrencyContext from "../core/CurrencyContext";

export default {
  components: { BookingTermIcon, SearchItemImages, Rating, CurrencyContext },
  mixins: [LiveaboardItemMixin],
  props: ["resort"],
  computed: {
    hasBookingTerm() {
      return typeof this.resort.__odyssey_booking_terms === "object";
    },
    bookingTerm() {
      return this.hasBookingTerm ? this.resort.__odyssey_booking_terms : {};
    },
  },
};
</script>

<style scoped>
.resort-item .price-cta {
  position: absolute;
  top: 0;
  right: 0;
  text-align: right;
}

.trip-item {
  display: grid;
  grid-template-columns: 95px 1fr 110px 110px 90px 10px 110px;
  grid-column-gap: 12px;
  height: 48px;
  line-height: 48px;
  padding-left: 24px;
  padding-right: 24px;
  border-bottom: 1px solid var(--black-light-2);
  border-right: 1px solid var(--black-light-2);
  border-left: 1px solid var(--black-light-2);
}

.trip-item:nth-child(2n + 1) {
  background-color: var(--black-light-1);
}

.trip-item:last-child {
  border-bottom: 1px solid var(--black-light-2);
  border-bottom-left-radius: 0.25rem;
  border-bottom-right-radius: 0.25rem;
}

.trip-item:first-child {
  border-top: 1px solid var(--black-light-2);
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
}

.trip-item.trip-discounted {
  border-color: var(--discount-primary);
  background-color: var(--discount-basis-bg);
  min-height: 80px;
  line-height: 80px;
}

.trip-item.trip-discounted .price-box:not([data-discount-method="custom"]) {
  top: 10px;
}

.trip-item:not(.trip-discounted) + .trip-item.trip-discounted {
  border-top: 1px solid var(--discount-primary);
}

.trip-item.trip-discounted .discount-tag {
  white-space: nowrap;
  position: absolute;
  background-color: var(--discount-primary);
  color: white;
  font-size: 12px;
  right: -24px;
  border-top-right-radius: 4px;
  border-bottom-left-radius: 4px;
  line-height: 16px;
  padding: 1px 10px;
}

.trip-item:not(:first-child) .discount-tag {
  border-top-right-radius: 0px;
  border-bottom-right-radius: 0px;
  border-top-left-radius: 0px;
}

.old-price {
  position: absolute;
  top: 10px;
  left: 0px;
  line-height: 16px;
  font-size: 12px;
  color: var(--black-light-5);
}

@media (min-width: 601px) and (max-width: 1200px) {
  .trip-item {
    grid-template-columns: 90px 2.5fr 90px minmax(120px, max-content) 120px;
  }

  .trip-item .availablity-text {
    line-height: 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
}
</style>
